import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { InventoryProvider } from './context/InventoryContext';
import { LayoutProvider } from './context/LayoutContext';
import ProtectedLayout from './components/ProtectedLayout';
import Dashboard from './pages/Dashboard';
import TasksBoard from './pages/TasksBoard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import TaskDetail from './pages/TaskDetail';
import Client from './pages/Client';
import ClientDetail from './pages/ClientDetail';
import LeadDetail from './pages/LeadDetail';
import Inventory from './pages/Inventory';
import ProductDetail from './pages/ProductDetail';
import RobotDetail from './pages/RobotDetail';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Robots from './pages/Robots';
import BusinessDailyTasks from './pages/BusinessDailyTasks';
import RoboticsProposalDetail from './pages/RoboticsProposalDetail';
import FileDrive from './pages/FileDrive';
import Contract from './pages/Contract';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailRequired from './pages/VerifyEmailRequired';
import Settings from './pages/Settings';
import Employees from './pages/Employees';
import SubmitFeedback from './pages/SubmitFeedback';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import EventIntakeFullScreen from './pages/EventIntakeFullScreen';
import Elevators from './pages/Elevators';
import ElevatorAppointmentDetail from './pages/ElevatorAppointmentDetail';
import ElevatorInitialSurveyDetail from './pages/ElevatorInitialSurveyDetail';
import ElevatorSurvey from './pages/ElevatorSurvey';
import ElevatorConflict from './pages/ElevatorConflict';
import Marketing from './pages/Marketing';
import OnsiteServiceDetail from './pages/OnsiteServiceDetail';
import Installation from './pages/Installation';
import InstallationAppointmentDetail from './pages/InstallationAppointmentDetail';
import SiteInspectionDetail from './pages/SiteInspectionDetail';
import CustomerSignOffDetail from './pages/CustomerSignOffDetail';
import InstallationConflict from './pages/InstallationConflict';
import './App.css';

/**
 * Service and support now lives inside Installation, but reminder emails sent
 * before the move still link to /service-support/onsite/:id.
 */
const LegacyOnsiteServiceRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={`/installation/onsite-service/${id}`} replace />;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <InventoryProvider>
        <LayoutProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-email-required" element={<VerifyEmailRequired />} />
          <Route path="/contract/:contractId" element={<Contract />} />
          <Route path="/installation-conflict/:token" element={<InstallationConflict />} />
          <Route path="/elevator-conflict/:token" element={<ElevatorConflict />} />
          <Route path="/elevator-survey/:token" element={<ElevatorSurvey />} />
          <Route element={<ProtectedLayout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/tasks" element={<TasksBoard />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:projectId" element={<ProjectDetail />} />
            <Route path="/tasks/add" element={<Navigate to="/tasks" replace />} />
            <Route path="/tasks/:taskId" element={<TaskDetail />} />
            <Route path="/client" element={<Client />} />
            <Route path="/client/:clientId" element={<ClientDetail />} />
            <Route path="/lead/:leadId" element={<LeadDetail />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/inventory/product/:productId" element={<ProductDetail />} />
            <Route path="/inventory/product/:productId/robot/:serialNumber" element={<RobotDetail />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:eventId/intake" element={<EventIntakeFullScreen />} />
            <Route path="/events/:eventId" element={<EventDetail />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/new" element={<Navigate to="/orders" replace />} />
            <Route path="/orders/:orderNumber" element={<OrderDetail />} />
            <Route path="/elevators" element={<Elevators />} />
            <Route path="/elevators/survey/:id" element={<ElevatorInitialSurveyDetail />} />
            <Route path="/elevators/:id" element={<ElevatorAppointmentDetail />} />
            <Route path="/service-support/onsite/:id" element={<LegacyOnsiteServiceRedirect />} />
            <Route path="/service-support" element={<Navigate to="/installation?tab=service-support" replace />} />
            <Route path="/installation" element={<Installation />} />
            <Route path="/installation/onsite-service/:id" element={<OnsiteServiceDetail />} />
            <Route path="/installation/site-inspection/:id" element={<SiteInspectionDetail />} />
            <Route path="/installation/sign-off/:id" element={<CustomerSignOffDetail />} />
            <Route path="/installation/:id" element={<InstallationAppointmentDetail />} />
            <Route path="/marketing" element={<Marketing />} />
            <Route
              path="/marketing/robotics-proposal/:proposalId"
              element={<RoboticsProposalDetail />}
            />
            <Route path="/robots" element={<Robots />} />
            <Route path="/robots/daily-tasks/:businessId" element={<BusinessDailyTasks />} />
            <Route path="/file-drive" element={<FileDrive />} />
            <Route path="/employees" element={<Employees />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/feedback" element={<SubmitFeedback />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </LayoutProvider>
        </InventoryProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;


